import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { Eye, FileDown, Plus, Scale, Trash2 } from 'lucide-react'
import { FibuUebergabenListe } from '../components/invoices/fibu-uebergaben-liste'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { TableShell } from '../components/ui/table-shell'
import { api, type InvoiceListItem, type InvoiceStatus } from '../lib/api'
import { formatCurrency } from '../lib/format'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices',
  component: InvoicesPage,
})

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Entwurf',
  sent: 'Versendet',
  paid: 'Bezahlt',
  cancelled: 'Storniert',
}

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'bg-bg-overlay text-text-secondary',
  sent: 'bg-accent/15 text-accent',
  paid: 'bg-emerald-500/15 text-emerald-500',
  cancelled: 'bg-red-500/15 text-red-500',
}

function fmtDate(s: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('de-DE')
}

function InvoicesPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  // Mobile-Shell (<640): Titel oben + ＋ → Builder (/invoices/new).
  useMobilePageTitle('Rechnungen')
  const { data, isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.listInvoices(),
    enabled: !!meData?.user,
  })

  // #1007: Schalter EMBERFIBU_RECHNUNGEN. aus = bisheriger Builder allein,
  // an = zusätzlich „Zeiten abrechnen“ (emberfibu), exklusiv = nur emberfibu.
  const { data: fibuStatus } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: !!meData?.user,
  })
  const fibuModus = fibuStatus?.modus ?? 'aus'
  const neuZiel = fibuModus === 'aus' ? '/invoices/new' : '/invoices/abrechnen'
  const fibuAktiv = fibuModus !== 'aus' && meData?.user?.role === 'admin'
  const { data: fibuData } = useQuery({
    queryKey: ['fibu-uebergaben'],
    queryFn: () => api.fibuUebergaben(),
    enabled: fibuAktiv,
  })
  const { data: kundenData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(false),
    enabled: fibuAktiv,
  })
  const abgleich = useMutation({
    mutationFn: (id: string) => api.fibuAbgleich(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fibu-uebergaben'] }),
    onError: (e: Error) => notify.error(e.message),
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InvoiceStatus }) =>
      api.updateInvoice(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      notify.success('Status aktualisiert')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      notify.success('Rechnung gelöscht')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  const invoices = data?.invoices ?? []
  const hatFibuUebergaben = fibuAktiv && (fibuData?.uebergaben.length ?? 0) > 0

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neue Rechnung → Builder. */}
      <MobileBottomSlots>
        <BarAccentPlus
          ariaLabel={fibuModus === 'aus' ? 'Neue Rechnung' : 'Zeiten abrechnen'}
          onClick={() => navigate({ to: neuZiel })}
        />
      </MobileBottomSlots>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Rechnungen"
        actions={
          <div className="flex items-center gap-2">
            {fibuModus !== 'exklusiv' && (
              <Link to="/invoices/new">
                <Button size="lg" variant={fibuModus === 'an' ? 'secondary' : undefined}>
                  <Plus className="h-4 w-4" /> Neue Rechnung
                </Button>
              </Link>
            )}
            {fibuModus !== 'aus' && (
              <Link to="/invoices/abrechnen">
                <Button size="lg">
                  <Scale className="h-4 w-4" /> Zeiten abrechnen
                </Button>
              </Link>
            )}
          </div>
        }
      />

      {fibuAktiv && (
        <FibuUebergabenListe
          uebergaben={fibuData?.uebergaben ?? []}
          kundenNamen={Object.fromEntries((kundenData?.customers ?? []).map((c) => [c.id, c.name]))}
          onAbgleich={(id) => abgleich.mutate(id)}
        />
      )}

      {isLoading ? (
        <p className="text-text-secondary">Lade …</p>
      ) : invoices.length === 0 && hatFibuUebergaben ? null : invoices.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
          <p className="text-text-secondary">
            Noch keine Rechnungen. Erstelle die erste im Builder.
          </p>
          <Link to={neuZiel} className="mt-4 inline-block">
            <Button>
              <Plus className="h-4 w-4" />{' '}
              {fibuModus === 'aus' ? 'Builder öffnen' : 'Zeiten abrechnen'}
            </Button>
          </Link>
        </div>
      ) : (
        <TableShell className="rounded-2xl bg-bg-elevated/40">
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/40">
              <tr className="text-left text-xs uppercase tracking-wider text-text-tertiary">
                <th className="px-4 py-3 font-medium">Nr.</th>
                <th className="px-4 py-3 font-medium">Kunde</th>
                <th className="px-4 py-3 font-medium">Datum</th>
                <th className="px-4 py-3 font-medium">Fällig</th>
                <th className="px-4 py-3 font-medium text-right">Betrag</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {invoices.map((inv) => (
                <InvoiceRow
                  key={inv.id}
                  inv={inv}
                  onStatusChange={(status) => updateStatusMutation.mutate({ id: inv.id, status })}
                  onDelete={async () => {
                    const ok = await confirm({
                      title: 'Rechnung löschen?',
                      description: `${inv.invoiceNumber} wird unwiderruflich gelöscht.`,
                      confirmLabel: 'Löschen',
                      variant: 'danger',
                    })
                    if (ok) deleteMutation.mutate(inv.id)
                  }}
                  isAdmin={meData.user.role === 'admin'}
                />
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  )
}

function InvoiceRow({
  inv,
  onStatusChange,
  onDelete,
  isAdmin,
}: {
  inv: InvoiceListItem
  onStatusChange: (s: InvoiceStatus) => void
  onDelete: () => void
  isAdmin: boolean
}) {
  return (
    <tr className="hover:bg-bg-overlay/30 transition-colors">
      <td className="px-4 py-3 font-mono text-text-primary">{inv.invoiceNumber}</td>
      <td className="px-4 py-3 text-text-primary">{inv.customerName ?? '—'}</td>
      <td className="px-4 py-3 text-text-secondary">{fmtDate(inv.issueDate)}</td>
      <td className="px-4 py-3 text-text-secondary">{fmtDate(inv.dueDate)}</td>
      <td className="px-4 py-3 text-right font-medium text-text-primary tabular-nums">
        {formatCurrency(inv.totalCents, inv.currency)}
      </td>
      <td className="px-4 py-3">
        <Select
          value={inv.status}
          onChange={(e) => onStatusChange(e.target.value as InvoiceStatus)}
          className={`h-8 text-xs ${STATUS_COLORS[inv.status]}`}
        >
          {(Object.keys(STATUS_LABELS) as InvoiceStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-1">
          <Link to="/invoices/$id" params={{ id: inv.id }}>
            <Button variant="ghost" size="sm" title="Detail anzeigen">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
          <a href={api.invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="sm" title="PDF öffnen">
              <FileDown className="h-4 w-4" />
            </Button>
          </a>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={onDelete} title="Löschen">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}
